import React from 'react';
import {
  Button,
  Card,
  CardHeader,
  Title3,
  Body1,
  Spinner,
  makeStyles,
  tokens,
  Text,
} from '@fluentui/react-components';
import { PersonCircle24Regular, SignOut24Regular } from '@fluentui/react-icons';
import { useAuth } from '../auth';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: tokens.spacingHorizontalXXL,
    backgroundColor: '#0a1b45',
  },
  card: {
    maxWidth: '420px',
    width: '100%',
    padding: tokens.spacingHorizontalXL,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    borderRadius: '12px',
  },
  logo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalXL,
  },
  logoIcon: {
    width: '80px',
    height: '80px',
    marginBottom: tokens.spacingVerticalM,
    objectFit: 'contain',
  },
  logoTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    textAlign: 'center',
    marginBottom: tokens.spacingVerticalXS,
  },
  logoSubtitle: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
    textAlign: 'center',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalL,
  },
  icon: {
    fontSize: '48px',
    color: '#742774',
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    marginTop: tokens.spacingVerticalL,
    padding: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
  },
  description: {
    textAlign: 'center',
    color: tokens.colorNeutralForeground2,
    marginBottom: tokens.spacingVerticalL,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: tokens.spacingVerticalL,
  },
  signInButton: {
    background: 'linear-gradient(135deg, #742774 0%, #572057 100%)',
    border: 'none',
    padding: '12px 32px',
    fontSize: '16px',
    fontWeight: '600',
    ':hover': {
      background: 'linear-gradient(135deg, #8a3d8a 0%, #742774 100%)',
    },
  },
  footer: {
    marginTop: tokens.spacingVerticalXL,
    textAlign: 'center',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
});

interface LoginButtonProps {
  onLoginSuccess?: () => void;
}

export const LoginButton: React.FC<LoginButtonProps> = ({ onLoginSuccess }) => {
  const styles = useStyles();
  const { isAuthenticated, user, login, logout, inProgress } = useAuth();

  const handleLogin = async () => {
    try {
      await login();
      onLoginSuccess?.();
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  if (inProgress) {
    return (
      <div className={styles.container}>
        <Spinner size="large" label="Signing you in..." />
      </div>
    );
  }

  if (isAuthenticated && user) {
    return (
      <div className={styles.container}>
        <Card className={styles.card}>
          <CardHeader
            header={
              <div className={styles.header}>
                <PersonCircle24Regular className={styles.icon} />
                <Title3>Welcome!</Title3>
              </div>
            }
          />
          <div className={styles.userInfo}>
            <Body1><strong>Name:</strong> {user.name}</Body1>
            <Body1><strong>Email:</strong> {user.username}</Body1>
          </div>
          <div className={styles.buttonContainer}>
            <Button
              appearance="secondary"
              icon={<SignOut24Regular />}
              onClick={logout}
            >
              Sign Out
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <div className={styles.logo}>
          <img src="/dynamics-logo.png" alt="Dynamics 365" className={styles.logoIcon} />
          <div className={styles.logoTitle}>ERP Agent Prompt Library</div>
          <div className={styles.logoSubtitle}>For Dynamics 365 Finance and Operations</div>
        </div>
        <Body1 className={styles.description}>
          Sign in with your Microsoft account to access AI-powered prompts for Finance, Supply Chain, Sales, and HR.
        </Body1>
        <div className={styles.buttonContainer}>
          <Button
            appearance="primary"
            size="large"
            onClick={handleLogin}
            className={styles.signInButton}
          >
            Sign in with Microsoft
          </Button>
        </div>
        <div className={styles.footer}>
          Secure sign-in powered by Microsoft Entra ID
        </div>
      </Card>
    </div>
  );
};
